(() => {
  'use strict';
  const theme = window.theme || {};
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const focusables = (root) => qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary,[tabindex]:not([tabindex="-1"])', root).filter((node) => node.getClientRects().length);
  const sectionCleanups = new WeakMap();
  const mobileMenus = new WeakMap();
  const announcements = new WeakMap();

  function onSectionUnload(node, cleanup) {
    const section = node.closest('.shopify-section') || node;
    const cleanups = sectionCleanups.get(section) || [];
    cleanups.push(cleanup);
    sectionCleanups.set(section, cleanups);
  }

  function lockPage(locked) {
    const anotherDialog = qs('.drawer[aria-hidden="false"],[data-mobile-menu][aria-hidden="false"],.quickview[aria-hidden="false"]');
    document.documentElement.classList.toggle('is-locked', locked || Boolean(anotherDialog));
  }

  function trapFocus(event, root, close) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key !== 'Tab') return;
    const items = focusables(root);
    if (!items.length) return;
    const first = items[0]; const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function initMobileMenu() {
    qsa('[data-mobile-menu]').forEach((drawer) => {
      const openButton = qsa('[data-mobile-menu-toggle]').find((button) => button.getAttribute('aria-controls') === drawer.id);
      if (!openButton || drawer.dataset.ready) return;
      drawer.dataset.ready = 'true';
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      const desktop = matchMedia('(min-width: 1400px)');
      let previousFocus;
      const close = (restoreFocus = true) => {
        const wasOpen = drawer.getAttribute('aria-hidden') === 'false';
        drawer.setAttribute('aria-hidden', 'true'); drawer.inert = true;
        openButton.setAttribute('aria-expanded', 'false');
        qsa('details[open]', drawer).forEach((group) => { group.open = false; });
        if (wasOpen) lockPage(false);
        if (wasOpen && restoreFocus && previousFocus?.isConnected && previousFocus.getClientRects().length) previousFocus.focus();
      };
      const open = () => {
        if (desktop.matches || drawer.getAttribute('aria-hidden') === 'false') return;
        previousFocus = document.activeElement; drawer.inert = false;
        drawer.setAttribute('aria-hidden', 'false'); openButton.setAttribute('aria-expanded', 'true');
        lockPage(true); qs('[data-mobile-menu-close]', drawer)?.focus();
      };
      openButton.addEventListener('click', open, options);
      qsa('[data-mobile-menu-close],[data-mobile-menu-overlay]', drawer).forEach((button) => button.addEventListener('click', () => close(), options));
      drawer.addEventListener('keydown', (event) => trapFocus(event, drawer, close), options);
      drawer.addEventListener('click', (event) => { if (event.target.closest('a[href]')) close(false); }, options);
      const changeMode = () => { if (desktop.matches) close(false); };
      desktop.addEventListener('change', changeMode);
      mobileMenus.set(drawer, { open, close });
      onSectionUnload(drawer, () => { listeners.abort(); desktop.removeEventListener('change', changeMode); close(false); mobileMenus.delete(drawer); delete drawer.dataset.ready; });
    });
  }

  function initMegaMenus() {
    const header = qs('[data-header]'); if (!header || header.dataset.megaReady) return;
    header.dataset.megaReady = 'true';
    const listeners = new AbortController();
    const options = { signal: listeners.signal };
    const groups = qsa('[data-mega-group]', header);
    const timers = new Map();
    const closeAll = () => groups.forEach((group) => { clearTimeout(timers.get(group)); group.open = false; });
    groups.forEach((group) => {
      const summary = group.querySelector('summary');
      const open = () => { closeAll(); group.open = true; };
      const close = () => {
        clearTimeout(timers.get(group));
        timers.set(group, setTimeout(() => { if (!group.contains(document.activeElement) && !group.dataset.editorSelected) group.open = false; }, 120));
      };
      group.addEventListener('mouseenter', () => { if (matchMedia('(hover: hover) and (pointer: fine)').matches) open(); }, options);
      group.addEventListener('mouseleave', close, options);
      group.addEventListener('focusout', close, options);
      summary?.addEventListener('click', (event) => { event.preventDefault(); const expanded = group.open; closeAll(); group.open = !expanded; }, options);
      summary?.addEventListener('keydown', (event) => { if (event.key === 'ArrowDown') { event.preventDefault(); open(); qs('a[href]', group)?.focus(); } }, options);
      group.addEventListener('keydown', (event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeAll(); summary?.focus(); } }, options);
    });
    document.addEventListener('click', (event) => { if (!header.contains(event.target)) closeAll(); }, options);
    onSectionUnload(header, () => { listeners.abort(); closeAll(); delete header.dataset.megaReady; });
  }

  function initAnnouncements() {
    qsa('[data-announcement-bar]').forEach((bar) => {
      if (bar.dataset.ready) return; bar.dataset.ready = 'true';
      const items = qsa('[data-announcement-item]', bar);
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      let index = Math.max(0, items.findIndex((item) => !item.hidden));
      let timer; let selected = false; let hovered = false;
      const show = (next) => { index = next; items.forEach((item, position) => { item.hidden = position !== index; }); };
      const stop = () => { clearInterval(timer); timer = undefined; };
      const start = () => {
        stop();
        if (items.length < 2 || selected || hovered || bar.contains(document.activeElement) || document.hidden || reducedMotion.matches) return;
        timer = setInterval(() => show((index + 1) % items.length), Math.max(3000, Number(bar.dataset.interval || 5) * 1000));
      };
      bar.addEventListener('mouseenter', () => { hovered = true; stop(); }, options);
      bar.addEventListener('mouseleave', () => { hovered = false; start(); }, options);
      bar.addEventListener('focusin', stop, options);
      bar.addEventListener('focusout', (event) => { if (!bar.contains(event.relatedTarget)) { stop(); queueMicrotask(start); } }, options);
      document.addEventListener('visibilitychange', start, options);
      reducedMotion.addEventListener('change', start);
      announcements.set(bar, { select(id) { const next = items.findIndex((item) => item.dataset.announcementBlock === id); if (next >= 0) { selected = true; stop(); show(next); } }, deselect() { selected = false; start(); } });
      start();
      onSectionUnload(bar, () => { selected = true; stop(); listeners.abort(); reducedMotion.removeEventListener('change', start); announcements.delete(bar); delete bar.dataset.ready; });
    });
  }

  function money(value) {
    const cents = Number(value || 0);
    try { return new Intl.NumberFormat(theme.locale || 'en-US', { style: 'currency', currency: theme.currency || 'USD' }).format(cents / 100); }
    catch (_) { return `$${(cents / 100).toFixed(2)}`; }
  }

  function initPredictiveSearch() {
    qsa('[data-predictive-search-form]').forEach((form) => {
      if (form.dataset.ready) return; form.dataset.ready = 'true';
      const input = qs('input[type="search"]', form); const panel = qs('[data-predictive-search-results]', form);
      if (!input || !panel) return; let controller;
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      const hide = () => { panel.hidden = true; };
      input.addEventListener('input', async () => {
        const term = input.value.trim(); controller?.abort();
        if (term.length < 2) { panel.replaceChildren(); hide(); return; }
        panel.hidden = false; panel.textContent = theme.strings.searching || 'Searching…'; controller = new AbortController();
        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=hide`, { signal: controller.signal });
          if (!response.ok) throw new Error('search'); const data = await response.json();
          if (listeners.signal.aborted || input.value.trim() !== term) return; const products = data.resources?.results?.products || [];
          panel.replaceChildren();
          if (!products.length) { panel.textContent = theme.strings.noResults || 'No results'; return; }
          products.forEach((product) => {
            const link = document.createElement('a'); link.href = product.url; link.className = 'predictive-product';
            if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = ''; image.width = 64; image.height = 64; link.append(image); }
            const copy = document.createElement('span'); const vendor = document.createElement('small'); vendor.textContent = product.vendor || '';
            const title = document.createElement('strong'); title.textContent = product.title; const price = document.createElement('em'); const numericPrice = Number(product.price); price.textContent = Number.isFinite(numericPrice) ? money(Math.round(numericPrice * 100)) : (product.price || '');
            copy.append(vendor, title, price); link.append(copy); panel.append(link);
          });
          const all = document.createElement('a'); all.className = 'predictive-all'; all.href = `${theme.routes.search}?type=product&q=${encodeURIComponent(term)}`; all.textContent = `${theme.strings.viewAll || 'View all'} →`; panel.append(all);
        } catch (error) { if (error.name !== 'AbortError') hide(); }
      }, options);
      input.addEventListener('focus', () => { if (panel.childNodes.length) panel.hidden = false; }, options);
      form.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); event.stopPropagation(); input.focus(); hide(); return; }
        if (panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = qsa('a[href]', panel); if (!links.length) return;
        event.preventDefault();
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
        if (next < 0 || next >= links.length) input.focus(); else links[next].focus();
      }, options);
      document.addEventListener('pointerdown', (event) => { if (!form.contains(event.target)) hide(); }, options);
      onSectionUnload(form, () => { listeners.abort(); controller?.abort(); hide(); delete form.dataset.ready; });
    });
  }

  const WISHLIST_KEY = 'perfumesplug-wishlist-v2';
  function readWishlist() {
    try {
      const modernValue = localStorage.getItem(WISHLIST_KEY);
      const modern = JSON.parse(modernValue || '[]');
      if (modernValue !== null && Array.isArray(modern)) return modern.filter((item) => item && item.handle);
      const legacy = JSON.parse(localStorage.getItem('perfumesplug-saved') || '[]');
      if (Array.isArray(legacy)) return legacy.map((item) => typeof item === 'string' ? { handle: item } : item).filter((item) => item?.handle);
    } catch (_) { /* Storage can be unavailable in privacy mode. */ }
    return [];
  }
  function writeWishlist(items) { try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(items)); localStorage.removeItem('perfumesplug-saved'); } catch (_) { /* no-op */ } }
  function refreshWishlistUI() {
    const items = readWishlist(); const handles = new Set(items.map((item) => item.handle));
    qsa('[data-wishlist-count]').forEach((badge) => { badge.textContent = items.length; badge.hidden = !items.length; });
    qsa('[data-wishlist-trigger]').forEach((button) => { const active = handles.has(button.dataset.handle); button.setAttribute('aria-pressed', String(active)); button.classList.toggle('is-saved', active); const label = qs('span', button); if (label) label.textContent = active ? (button.dataset.removeLabel || theme.strings.removeWishlist || 'Remove from wishlist') : (button.dataset.saveLabel || theme.strings.saveWishlist || 'Save to wishlist'); });
  }
  function toggleWishlist(button) {
    const handle = button.dataset.handle; if (!handle) return;
    let items = readWishlist(); const index = items.findIndex((item) => item.handle === handle);
    if (index >= 0) items.splice(index, 1); else items.push({ handle, title: button.dataset.title || handle, vendor: button.dataset.vendor || '', url: button.dataset.url || `/products/${handle}`, image: button.dataset.image || '', price: Number(button.dataset.price || 0) });
    items = items.filter((item, position) => items.findIndex((candidate) => candidate.handle === item.handle) === position).slice(0, 100);
    writeWishlist(items); refreshWishlistUI(); document.dispatchEvent(new CustomEvent('wishlist:changed'));
  }
  async function loadWishlistPage() {
    const page = qs('[data-wishlist-page]'); if (!page || page.dataset.loaded) return; page.dataset.loaded = 'true';
    const grid = qs('[data-wishlist-grid]', page); const loading = qs('[data-wishlist-loading]', page); const empty = qs('[data-wishlist-empty]', page); const clear = qs('[data-wishlist-clear]', page); const summary = qs('[data-wishlist-summary]', page);
    const render = async () => {
      const items = readWishlist(); grid.replaceChildren(); loading.hidden = false; empty.hidden = true; clear.hidden = !items.length;
      summary.textContent = items.length === 1 ? (theme.strings.wishlistOne || '1 saved fragrance') : (theme.strings.wishlistMany || `${items.length} saved fragrances`).replace('__COUNT__', items.length);
      if (!items.length) { loading.hidden = true; empty.hidden = false; return; }
      const responses = await Promise.all(items.map(async (item) => { try { const response = await fetch(`/products/${encodeURIComponent(item.handle)}?section_id=wishlist-product-card`); return response.ok ? response.text() : ''; } catch (_) { return ''; } }));
      responses.forEach((html) => { if (!html.trim()) return; const range = document.createRange(); range.selectNode(grid); grid.append(range.createContextualFragment(html)); });
      loading.hidden = true; refreshWishlistUI(); document.dispatchEvent(new CustomEvent('theme:content:updated'));
      if (!grid.children.length) empty.hidden = false;
    };
    clear.addEventListener('click', () => { writeWishlist([]); refreshWishlistUI(); render(); });
    page.addEventListener('click', (event) => { if (event.target.closest('[data-wishlist-trigger]')) setTimeout(render, 0); });
    await render();
  }

  function initBrands() {
    qsa('[data-brands-page]').forEach((page) => {
      if (page.dataset.ready) return; page.dataset.ready = 'true';
      const input = qs('[data-brand-search]', page); const cards = qsa('[data-brand-card]', page); const empty = qs('[data-brand-empty]', page); let letter = 'all';
      const apply = () => { const term = (input?.value || '').trim().toLowerCase(); let shown = 0; cards.forEach((card) => { const match = (!term || card.dataset.brandName.includes(term)) && (letter === 'all' || card.dataset.brandLetterValue === letter); card.hidden = !match; if (match) shown += 1; }); empty.hidden = shown > 0; };
      input?.addEventListener('input', apply); qsa('[data-brand-letter]', page).forEach((button) => button.addEventListener('click', () => { letter = button.dataset.brandLetter; qsa('[data-brand-letter]', page).forEach((item) => item.classList.toggle('is-active', item === button)); apply(); }));
    });
    qsa('[data-facet-brand-search]').forEach((input) => { if (input.dataset.ready) return; input.dataset.ready = 'true'; input.addEventListener('input', () => { const term = input.value.trim().toLowerCase(); qsa('[data-brand-name]', input.closest('.facet')).forEach((link) => { link.hidden = term && !link.dataset.brandName.includes(term); }); }); });
  }

  function initShortlists() {
    qsa('[data-shortlist]').forEach((section) => {
      if (section.dataset.ready) return; section.dataset.ready = 'true';
      const tabs = qsa('[data-shortlist-tab]', section); const panels = qsa('[data-shortlist-panel]', section); const link = qs('[data-shortlist-link]', section);
      const select = (tab) => { tabs.forEach((item) => { const active = item === tab; item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; }); panels.forEach((panel) => { const active = panel.dataset.shortlistPanel === tab.dataset.shortlistTab; panel.hidden = !active; if (active && link) link.href = panel.dataset.panelUrl || link.href; }); document.dispatchEvent(new CustomEvent('theme:content:updated')); };
      tabs.forEach((tab, index) => { tab.addEventListener('click', () => select(tab)); tab.addEventListener('keydown', (event) => { if (!['ArrowLeft','ArrowRight'].includes(event.key)) return; event.preventDefault(); const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length; tabs[next].focus(); select(tabs[next]); }); });
    });
  }

  function initAddresses() {
    if (!window.Shopify?.CountryProvinceSelector) return;
    qsa('[data-address-country]').forEach((country) => { if (country.dataset.ready) return; country.dataset.ready = 'true'; const prefix = country.id.replace('Country', ''); const province = document.getElementById(`${prefix}Province`); if (!province) return; new Shopify.CountryProvinceSelector(country.id, province.id, { hideElement: province.closest('[data-address-province-wrap]') }); });
    qsa('[data-address-cancel]').forEach((button) => { if (button.dataset.ready) return; button.dataset.ready = 'true'; button.addEventListener('click', () => button.closest('details')?.removeAttribute('open')); });
  }

  document.addEventListener('click', (event) => { const button = event.target.closest('[data-wishlist-trigger]'); if (button) { event.preventDefault(); toggleWishlist(button); } });
  const init = () => { initMobileMenu(); initMegaMenus(); initAnnouncements(); initPredictiveSearch(); initBrands(); initShortlists(); initAddresses(); refreshWishlistUI(); loadWishlistPage(); };
  document.addEventListener('DOMContentLoaded', init); document.addEventListener('theme:content:updated', () => { initPredictiveSearch(); initBrands(); refreshWishlistUI(); });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    (sectionCleanups.get(section) || []).forEach((cleanup) => cleanup());
    sectionCleanups.delete(section);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    const bar = qs('[data-announcement-bar]', section);
    if (bar) announcements.get(bar)?.select(event.detail?.blockId);
    const mobile = matchMedia('(max-width: 1399px)').matches;
    const candidates = qsa('[data-header-block]', section).filter((node) => node.dataset.headerBlock === event.detail?.blockId);
    const target = candidates.find((node) => Boolean(node.closest('[data-mobile-menu]')) === mobile) || candidates[0] || event.target;
    const drawer = target.closest('[data-mobile-menu]');
    if (drawer) mobileMenus.get(drawer)?.open();
    const group = target.closest('[data-mega-group]');
    if (group) { qsa('[data-mega-group][open]', section).forEach((other) => { other.open = other === group; }); group.dataset.editorSelected = 'true'; group.open = true; }
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    const bar = qs('[data-announcement-bar]', section);
    if (bar) announcements.get(bar)?.deselect();
    qsa('[data-editor-selected]', section).forEach((group) => { delete group.dataset.editorSelected; group.open = false; });
  });
  window.PerfumesPlug = { ...(window.PerfumesPlug || {}), money, refreshWishlistUI, loadWishlistPage };
})();
